/* WRITE FIRST, BRILL WRAPS (her call, 2026-11-03; docs/newsletter-write-first-spec.md,
 * mockup docs/prototypes/newsletter-write.html).
 *
 * A blog post that is HER OWN WORDS: one full-screen page to write on, and a
 * ✦ Brill menu whose answers wait in a panel on the right — "pops out like the
 * Social preview, closed until you are ready". Nothing Brill says touches her
 * text until she presses Fix, Cut, Put it in or Put these in, one by one, and
 * every suggestion is checked against her text as it is NOW
 * (utils/newsletterShape.js): a spot she has since changed is said, not guessed.
 *
 * Her words live in the piece's one `writing` section ({ subtitle, text }); a
 * subhead Brill adds is a "## " line between her paragraphs. The short answer,
 * the claims, the FAQ and the ask go into their own sections, which the blog
 * page reads (api/_lib/blogPage.js).
 *
 * Saves as she leaves a field, and a moment after she stops typing (rule 1).
 */
import { useEffect, useRef, useState } from "react";
import { B, btnGhost, btnPrimary, input } from "./tokens";
import { SavedTick } from "./ui";
import { RecordFooter } from "./shell";
import { LibraryPickButton, ALL_LIBRARY_KINDS } from "./libraryPicker";
import { STORY_MINE, useLibrary, usedByStamp } from "../../utils/library";
import { newsletterLabel, newsletterToText, readMinutes } from "../../utils/newsletters";
import {
  SHAPE_MODES, titleOption, applyCut, applyInsert, applyReplace, applySubhead, changeView, checkShape, claimsText, faqText,
  parseShapeReply, shapeMode,
} from "../../utils/newsletterShape";
import { isOnBlog } from "../../utils/blogPublish";
import { useRecordKeep } from "../../utils/pageChat";
import { changeCount, checkPolishedPart, herQuotes, loadPolishOpts, mergeNotes, polishParts, savePolishOpts } from "../../utils/newsletterPolish";
import { PolishScreen, PolishSetup } from "./newsletterPolish";
import { MediaPickButton } from "./mediaPicker";

const POLISH_ITEM = { id: "polish", label: "Polish it…", hint: "Fixes, tightens and makes it search friendly, in your voice." };
const words = (s) => (String(s || "").trim().match(/\S+/g) || []).length;
const SAVE_AFTER_MS = 1500;

export function NewsletterWriter({
  newsletter, blocks = [], offers = [], pillars = [], error, isMobile = false,
  onBack, onSave, onSaveBlock, onEnsureBlock, onAskBrill, onPublish = null, onPreview = null,
  onMakeVariant, makingVariant = null, onDelete, newsletters = [],
}) {
  const writing = blocks.find((b) => b.blockType === "writing") || null;
  const [title, setTitle] = useState(newsletter?.name || "");
  const [subtitle, setSubtitle] = useState(writing?.content?.subtitle || "");
  const [text, setText] = useState(writing?.content?.text || "");
  const [saveProblem, setSaveProblem] = useState("");
  const textRef = useRef(null);
  const titleRef = useRef(null);
  const caret = useRef({ start: null, end: null });
  const timer = useRef(null);
  const latest = useRef({ title, subtitle, text });
  latest.current = { title, subtitle, text };

  /* The panel: which Brill task, and what came back. */
  const [panel, setPanel] = useState(null);         // null | "preview" | a mode id
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState(null);           // { ok, text }
  const [result, setResult] = useState(null);       // checkShape() output for `panel`
  const [done, setDone] = useState({});             // suggestion id → "yes" | "no" | "moved"
  const [askText, setAskText] = useState("");
  const [offerId, setOfferId] = useState(newsletter?.offerId || offers.find((o) => o.status !== "retired")?.id || "");
  const [picks, setPicks] = useState({});           // search: id → checked
  const [titlePick, setTitlePick] = useState(0);
  const [previewHtml, setPreviewHtml] = useState("");
  /* The preview, full screen (her ask, 2026-11-04): the side panel shows the
     blog page at phone width; this shows it as a reader on a laptop sees it. */
  const [previewFull, setPreviewFull] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [libSaid, setLibSaid] = useState("");
  const { save: saveLibraryItem, newId: newLibraryId } = useLibrary();

  /* ✦ Polish it: the pop-up, the screen, and the version kept while it waits
     (record_chats, under the piece's "page" key, so a section's chat on the
     older screen can never sweep it away). */
  const [polishSetup, setPolishSetup] = useState(false);
  const [polishOpts, setPolishOpts] = useState(loadPolishOpts);
  const [polish, setPolish] = useState(null);   // { original, version, extras, picks, turns, working, stage, problem }
  const [polishGone, setPolishGone] = useState(false);
  const [undoText, setUndoText] = useState(null);
  const polishRun = useRef(0);
  const [parked, setParked] = useState(null);   // a polish she walked away from never reopens the screen
  const keep = useRecordKeep({ tool: "newsletter", recordId: newsletter?.id, blockKey: null });

  /* ── Saving her words ─────────────────────────────────────────────────── */
  const saveWords = async (next = latest.current) => {
    clearTimeout(timer.current);
    if (!writing) return;
    const was = writing.content || {};
    if ((was.text || "") !== next.text || (was.subtitle || "") !== next.subtitle) {
      const ok = await onSaveBlock({ ...writing, content: { ...was, text: next.text, subtitle: next.subtitle } });
      setSaveProblem(ok ? "" : "Your words didn't save. Check your connection; they're still here.");
    }
    if ((newsletter.name || "") !== next.title) await onSave({ ...newsletter, name: next.title });
  };
  const soon = () => { clearTimeout(timer.current); timer.current = setTimeout(() => saveWords(), SAVE_AFTER_MS); };
  useEffect(() => () => clearTimeout(timer.current), []);
  /* A blog post with no sections yet gets its page the moment it opens. */
  /* Once only: a second ask while the first is still saving would make two. */
  const askedForPage = useRef(false);
  useEffect(() => {
    if (!newsletter || writing || askedForPage.current) return;
    askedForPage.current = true;
    onEnsureBlock("writing", { text: "", subtitle: "" });
  }, [newsletter, writing, onEnsureBlock]);

  /* A change from the panel lands in her text AND is saved at once. */
  const setAndSave = (nextText) => { setText(nextText); saveWords({ ...latest.current, text: nextText }); };

  /* Auto-height, so the page scrolls rather than a box inside it — the title
     too, which wraps when the panel takes the right side. */
  useEffect(() => {
    /* THE PAGE MUST NOT JUMP (her report, 2026-11-04: fixing a word in the
       last paragraph threw the page up so that word sat on the bottom line).
       Measuring means collapsing the box to "auto" for an instant; the page
       around it got shorter, the scroll was clamped, and it never came back.
       So the scroll position of whatever scrolls is held and put back. */
    const fit = () => {
      const kept = [];
      for (let p = textRef.current?.parentElement; p; p = p.parentElement) {
        if (p.scrollTop) kept.push([p, p.scrollTop]);
      }
      const winY = window.scrollY;
      for (const [el, pad] of [[textRef.current, 4], [titleRef.current, 0]]) {
        if (!el) continue;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight + pad}px`;
      }
      for (const [p, top] of kept) if (p.scrollTop !== top) p.scrollTop = top;
      if (window.scrollY !== winY) window.scrollTo(window.scrollX, winY);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [text, title, panel]);

  const remember = () => { const el = textRef.current; if (el) caret.current = { start: el.selectionStart, end: el.selectionEnd }; };

  /* ── The Library, in and out ─────────────────────────────────────────── */
  const insertRaw = (words_) => {
    const at = caret.current.start ?? text.length;
    const before = text.slice(0, at), after = text.slice(caret.current.end ?? at);
    const gapBefore = before && !/\n\n$/.test(before) ? (/\n$/.test(before) ? "\n" : "\n\n") : "";
    const gapAfter = after && !/^\n\n/.test(after) ? (/^\n/.test(after) ? "\n" : "\n\n") : "";
    setAndSave(`${before}${gapBefore}${String(words_).trim()}${gapAfter}${after}`);
  };
  const selection = () => {
    const { start, end } = caret.current;
    return start != null && end != null && end > start ? text.slice(start, end).trim() : "";
  };
  const saveSelectionToLibrary = async () => {
    const chosen = selection();
    if (!chosen) { setLibSaid("Select the part you want to keep first, then press this."); return; }
    const first = chosen.split(/\s+/).slice(0, 8).join(" ");
    const ok = await saveLibraryItem({
      id: newLibraryId(), kind: "story", text: chosen, title: first.length < chosen.length ? `${first}…` : first,
      whose: STORY_MINE, usedBy: [usedByStamp("newsletter", newsletter.id, newsletterLabel(newsletter))],
    });
    setLibSaid(ok ? "Saved to your Library as a story." : "That didn't reach your Library. Try again in a moment.");
  };

  /* ── The Brill menu ─────────────────────────────────────────────────── */
  const run = async (mode, extra = {}) => {
    await saveWords();
    setBusy(true); setSaid(null); setResult(null); setDone({}); setPicks({}); setTitlePick(0);
    try {
      const offer = offers.find((o) => o.id === (extra.offerId ?? offerId)) || null;
      const reply = await onAskBrill({ mode, writing: { text, subtitle }, offer, ask: extra.ask || "" });
      const data = parseShapeReply(reply);
      if (!data) { setSaid({ ok: false, text: "Brill answered, but not in a way I could read. Nothing in your piece changed; try again." }); return; }
      const checked = checkShape(mode, data, text);
      setResult(checked);
      if (mode === "search") {
        const on = {};
        for (const k of ["subheads", "claims", "faq"]) for (const s of checked[k] || []) on[s.id] = true;
        on.answer = Boolean(checked.answer);
        setPicks(on);
      }
    } catch (err) {
      setSaid({ ok: false, text: err?.message || "Brill didn't answer just then. Your piece wasn't touched." });
    } finally {
      setBusy(false);
    }
  };
  const openMode = (mode) => {
    setMenuOpen(false);
    setPanel(mode);
    setResult(null); setSaid(null);
    if (mode !== "ask" && mode !== "offer") run(mode);
  };
  const mark = (sid, v) => setDone((d) => ({ ...d, [sid]: v }));
  const tryApply = (sid, next) => {
    if (next == null) { mark(sid, "moved"); return; }
    setAndSave(next);
    mark(sid, "yes");
  };

  /* Search friendly: everything she ticked, in one press. */
  const putSearchIn = async () => {
    const r = result || {};
    let next = text;
    for (const s of r.subheads || []) if (picks[s.id]) next = applySubhead(next, s.before, s.text) ?? next;
    if (next !== text) setAndSave(next);
    const titleWas = newsletter.name || "";
    const chosenTitle = r.titles?.[titlePick];
    const patch = {};
    if (chosenTitle && chosenTitle !== titleWas) { patch.name = chosenTitle; setTitle(chosenTitle); }
    if (r.pillar && !newsletter.pillar && picks.pillar) patch.pillar = r.pillar;
    if (Object.keys(patch).length) await onSave({ ...newsletter, ...patch, name: patch.name ?? latest.current.title });
    if (r.answer && picks.answer) await onEnsureBlock("answer", { text: r.answer });
    const claims = (r.claims || []).filter((c) => picks[c.id]);
    if (claims.length) await onEnsureBlock("claims", { text: claimsText(claims) });
    const faq = (r.faq || []).filter((f) => picks[f.id]);
    if (faq.length) await onEnsureBlock("faq", { text: faqText(faq) });
    setSaid({ ok: true, text: "In. Your paragraphs are as you wrote them." });
    setResult(null);
  };

  const useNextStep = async (next) => {
    const offer = offers.find((o) => o.id === offerId);
    const link = String(offer?.linkUrl || "").trim();
    /* With a real link, Brill's "[add the link here]" placeholder goes and the
       link takes its place; without one, the placeholder stays so she sees it. */
    const said_ = link ? next.replace(/\s*\[[^\]]*\blink\b[^\]]*\]/i, "").trim() : next;
    await onEnsureBlock("ask", { text: link ? `${said_} ${link}` : said_ });
    if (offer && newsletter.offerId !== offer.id) await onSave({ ...newsletter, name: latest.current.title, offerId: offer.id });
    setSaid({ ok: true, text: link ? "That's the next step, with your offer's link." : "That's the next step. Your offer has no link yet; add one on the Offer Ladder." });
  };

  const showPreview = async () => {
    setMenuOpen(false);
    setPanel("preview");
    setPreviewHtml("");
    await saveWords();
    const r = await onPreview(newsletter);
    setPreviewHtml(r.ok ? r.html : "");
    if (!r.ok) setSaid({ ok: false, text: r.error });
  };

  /* ── ✦ Polish it ──────────────────────────────────────────────────────── */
  const keepPolish = (p, done = false) => keep.save("record", { turns: p.turns || [], pending: done ? { done: true } : { original: p.original, version: p.version, extras: p.extras, picks: p.picks, titleCard: p.titleCard || [] } });
  const short = (s) => `“${String(s).trim().split(/\s+/).slice(0, 6).join(" ")}…”`;

  const startPolish = async () => {
    const opts = polishOpts;
    savePolishOpts(opts);
    setPolishSetup(false); setMenuOpen(false); setPanel(null); setUndoText(null);
    await saveWords();
    const original = latest.current.text;
    const run = (polishRun.current += 1);
    const live = () => polishRun.current === run;
    const base = { original, version: "", extras: null, picks: {}, turns: [], working: true, stage: "Brill is polishing your piece…", problem: "" };
    setPolish(base);

    /* 1. Every part at once, each held to her level. */
    const parts = polishParts(original);
    const done_ = await Promise.all(parts.map(async (part) => {
      if (!part.trim()) return { text: part, notes: [] };
      try {
        const data = parseShapeReply(await onAskBrill({ mode: "polish", writing: { text: part, subtitle }, polish: { part, whole: parts.length > 1 ? original : "", opts } }));
        if (!data) return { text: part, failed: true, notes: [`Brill's answer for the part starting ${short(part)} couldn't be read, so it is left as you wrote it.`] };
        return checkPolishedPart(part, data, opts.level);
      } catch (err) {
        return { text: part, failed: true, error: err?.message, notes: [`Brill didn't answer for the part starting ${short(part)}, so it is left as you wrote it.`] };
      }
    }));
    if (!live()) return;
    if (done_.every((r) => r.failed || !r.text.trim())) {
      setPolish({ ...base, working: false, problem: done_.find((r) => r.error)?.error || "Brill didn't answer just then. Your draft wasn't touched; try again." });
      return;
    }
    let version = done_.map((r) => r.text).join("");
    const notes = done_.map((r) => r.notes || []);

    /* 2. Search and the offer, on the polished version. */
    const offer = opts.offer ? offers.find((o) => o.id === offerId) || null : null;
    if (!live()) return;
    if (opts.search || offer) setPolish((p) => ({ ...p, stage: opts.search ? "Adding the search bits…" : "Weaving in your offer…" }));
    const ask = async (mode) => {
      try { return parseShapeReply(await onAskBrill({ mode, writing: { text: version, subtitle }, offer })); }
      catch { return null; }
    };
    const [searchData, offerData] = await Promise.all([opts.search ? ask("search") : null, offer ? ask("offer") : null]);
    if (!live()) return;
    const extras = {}, picks = {};
    if (opts.search && !searchData) notes.push(["The search bits didn't come back this time. Your polish is all here; you can polish again for them."]);
    if (searchData) {
      const r = checkShape("search", searchData, version);
      for (const s of r.subheads || []) version = applySubhead(version, s.before, s.text) ?? version;
      Object.assign(extras, { titles: r.titles || [], answer: r.answer, claims: r.claims || [], faq: r.faq || [], pillar: newsletter.pillar ? "" : r.pillar });
      /* Her title stays unless she has none: Brill's go in a card in the chat. */
      if (!latest.current.title.trim() && r.titles?.[0]) picks.title = r.titles[0];
      extras.titleCard = (r.titles || []).map(titleOption).filter((t) => t.title && t.title !== latest.current.title);
      picks.answer = Boolean(r.answer);
      for (const c of [...(r.claims || []), ...(r.faq || [])]) picks[c.id] = true;
      notes.push((r.gaps || []).map((g) => `Your piece raises “${g.q}” without answering it. Brill won't answer it for you.`));
    }
    if (offer && !offerData) notes.push(["The offer didn't come back this time. Everything else is here."]);
    if (offerData) {
      const r = checkShape("offer", offerData, version);
      for (const x of r.inserts || []) version = applyInsert(version, x.after, x.text) ?? version;
      if (r.next) { extras.next = r.next; picks.next = true; }
    }

    version = herQuotes(original, version);   // subheads and offer lines too
    const said_ = mergeNotes(notes);
    const count = changeCount(original, version);
    const opener = `I polished it: ${count} ${count === 1 ? "change" : "changes"}, ${words(original)} → ${words(version)} words. Press a highlighted part to see your own words and put them back, or tell me here what to change.`
      + (said_.length ? `\n\nA few things only you can decide:\n• ${said_.join("\n• ")}` : "");
    const ready = { original, version, extras, picks, titleCard: extras.titleCard || [], turns: [{ role: "assistant", content: opener }], working: false, stage: "", problem: "" };
    setPolish(ready);
    setParked(null);
    setPolishGone(false);
    keepPolish(ready);
  };

  /* Her word in the chat: Brill's edits land on the version, never her draft. */
  const sendPolish = async (msg) => {
    const p = polish;
    const run = polishRun.current;
    const turns = [...p.turns, { role: "user", content: msg }];
    setPolish({ ...p, turns, working: true });
    let next;
    try {
      /* A CHAT, not a form (her read, 2026-11-04): Brill sees the last few
         turns, so "and the ending?" means something. */
      const earlier = p.turns.slice(1).slice(-8).map((t) => `${t.role === "user" ? "She" : "Brill"}: ${t.content}`).join("\n\n");
      const ask = earlier ? `Earlier in this chat:\n${earlier}\n\nNow she says: ${msg}` : msg;
      const data = parseShapeReply(await onAskBrill({ mode: "ask", writing: { text: p.version, subtitle }, original: p.original !== p.version ? p.original : "", ask }));
      const r = checkShape("ask", data || {}, p.version);
      let v = p.version, made = 0;
      for (const e of r.edits || []) { const t = applyReplace(v, e.find, e.replace); if (t != null) { v = t; made += 1; } }
      v = herQuotes(p.original, v);
      const reply = data ? (r.reply || (made ? "Done." : "I didn't change anything.")) : "I couldn't read my own answer just then. Nothing changed; try again.";
      next = { ...p, version: v, titleCard: r.titles?.length ? r.titles : p.titleCard || [], turns: [...turns, { role: "assistant", content: made ? `${reply}\n\n(${made} ${made === 1 ? "change" : "changes"} made on the right.)` : reply }], working: false };
    } catch (err) {
      next = { ...p, turns: [...turns, { role: "assistant", content: err?.message || "Brill didn't answer just then. Nothing changed." }], working: false };
    }
    if (polishRun.current === run) setPolish(next); else setParked(next);
    keepPolish(next);
  };

  /* Ask Brill is the same screen with no polish run: her piece on the right,
     unchanged, and a chat on the left. Whatever Brill changes shows marked. */
  const openAskChat = async () => {
    setMenuOpen(false); setPanel(null); setUndoText(null);
    await saveWords();
    const t = latest.current.text;
    const live = waiting && waiting.original === t ? waiting : null;
    setParked(null);
    setPolish(live || { original: t, version: t, extras: null, picks: {}, working: false, stage: "", problem: "",
      turns: [{ role: "assistant", content: "What would you like to do with this piece? Ask me anything about it, or tell me what to change. Any change I make shows on the right, for you to keep, edit or put back." }] });
  };

  const setPolishVersion = (v) => { const next = { ...polish, version: v }; setPolish(next); keepPolish(next); };
  const setPolishPicks = (fn) => setPolish((p) => ({ ...p, picks: typeof fn === "function" ? fn(p.picks) : fn }));

  /* Use this version: the words go into her draft, and what she ticked goes
     around them — the same places Put these in and the next step use. */
  const takePolish = async () => {
    const p = polish;
    const before = latest.current.text;
    const name = String(p.picks.title ?? "").trim() || latest.current.title;
    setText(p.version);
    if (name !== latest.current.title) setTitle(name);
    const sub = p.picks.subtitle ?? latest.current.subtitle;
    if (sub !== latest.current.subtitle) setSubtitle(sub);
    await saveWords({ ...latest.current, text: p.version, title: name, subtitle: sub });
    const x = p.extras || {};
    if (x.pillar && p.picks.pillar) await onSave({ ...newsletter, name, pillar: x.pillar });
    if (x.answer && p.picks.answer) await onEnsureBlock("answer", { text: x.answer });
    const claims = (x.claims || []).filter((c) => p.picks[c.id]);
    if (claims.length) await onEnsureBlock("claims", { text: claimsText(claims) });
    const faq = (x.faq || []).filter((f) => p.picks[f.id]);
    if (faq.length) await onEnsureBlock("faq", { text: faqText(faq) });
    if (x.next && p.picks.next) await useNextStep(x.next);
    keepPolish(p, true);
    setParked(null);
    setPolishGone(true);
    setUndoText(before);
    setPolish(null);
  };
  /* A polished version she has not used yet: the one she just stepped back
     from, or the one kept from an earlier visit. */
  const kept = keep.loaded && keep.record?.pending?.version && !keep.record.pending.done
    ? { ...keep.record.pending, turns: keep.record.turns || [], working: false, stage: "", problem: "" } : null;
  const waiting = !polish && !polishGone ? parked || kept : null;
  const discardPolish = () => { if (waiting) keepPolish(waiting, true); setParked(null); setPolishGone(true); };

  if (!newsletter) return null;
  const n = words(text) + words(subtitle);
  const state = busy ? "reading" : null;

  if (polish) {
    return (
      <PolishScreen original={polish.original} version={polish.version} setVersion={setPolishVersion}
        extras={polish.extras} picks={polish.picks} setPicks={setPolishPicks} turns={polish.turns}
        working={polish.working} stage={polish.stage} problem={polish.problem}
        draftChanged={!polish.working && polish.original !== text} currentTitle={title} currentSubtitle={subtitle} isMobile={isMobile}
        titleCard={polish.titleCard || []} onDismissTitles={() => setPolish((p) => ({ ...p, titleCard: [] }))}
        onSend={sendPolish} onUse={takePolish}
        onBack={() => { polishRun.current += 1; if (polish.version) setParked(polish); setPolish(null); }} />
    );
  }

  /* ── The page ──────────────────────────────────────────────────────── */
  const panelOpen = Boolean(panel);
  const full = panel === "preview" && previewFull;
  const panelStyle = isMobile || full
    ? { position: "fixed", inset: 0, zIndex: full ? 120 : 60, background: B.surface, display: "flex", flexDirection: "column" }
    : { width: 430, flex: "none", borderLeft: `1px solid ${B.border}`, background: B.surface, display: "flex", flexDirection: "column", position: "sticky", top: 0, maxHeight: "calc(100vh - 120px)", alignSelf: "flex-start" };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", minHeight: "100%" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "4px 0 12px", borderBottom: `1px solid ${B.border}` }}>
          <button onClick={async () => { await saveWords(); onBack(); }}
            style={{ border: "none", background: "none", color: B.mid, padding: "4px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
            ← All pieces
          </button>
          <span style={{ fontSize: 11.5, color: B.faint }}>{n ? `${n} words · ${readMinutes([{ blockType: "writing", content: { text } }])} min read` : "Nothing written yet"}</span>
          <SavedTick problem={saveProblem || (error ? "That didn't save — try again" : "")} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <LibraryPickButton kinds={ALL_LIBRARY_KINDS} label="+ From your Library"
              usedAs={usedByStamp("newsletter", newsletter.id, newsletterLabel(newsletter))}
              onPick={(item) => insertRaw(item.text || "")} />
            {onPreview && (
              <button onClick={showPreview} style={{ ...btnGhost, padding: "7px 13px", fontSize: 12.5 }}>Preview</button>
            )}
            <span style={{ position: "relative" }}>
              <button onClick={() => setMenuOpen((v) => !v)} aria-haspopup="menu" aria-expanded={menuOpen}
                style={{ ...btnPrimary, padding: "7px 14px", fontSize: 12.5 }}>
                ✦ Brill
              </button>
              {menuOpen && (
                <div role="menu" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 30, width: 280, background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 10, boxShadow: "0 12px 30px rgba(26,26,46,.16)", padding: 6 }}>
                  {/* Two, by her call 2026-11-04: Polish it does what Fix, Tighten,
                      Search and the offer did one press at a time. */}
                  {[POLISH_ITEM, ...SHAPE_MODES.filter((m) => m.id === "ask")].map((m) => (
                    <button key={m.id} role="menuitem" onClick={() => (m.id === "polish" ? (setMenuOpen(false), setPolishSetup(true)) : m.id === "ask" ? openAskChat() : openMode(m.id))}
                      style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer", fontFamily: "inherit", borderTop: m.id === "ask" ? `1px solid ${B.border}` : "none" }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: B.text }}>{m.label}</span>
                      <span style={{ display: "block", fontSize: 11.5, color: B.dim }}>{m.hint}</span>
                    </button>
                  ))}
                </div>
              )}
            </span>
          </div>
        </div>

        {(waiting || undoText != null) && (
          <div role="status" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "12px 0 0", padding: "9px 12px", borderRadius: 9, fontSize: 12.5, background: waiting ? B.tealQuiet : B.surface2, color: waiting ? B.teal : B.mid }}>
            {waiting ? (<>
              <span style={{ flex: 1, minWidth: 180 }}>{waiting.version !== waiting.original ? "Brill's version is waiting." : "Your chat with Brill is waiting."} Your draft below is as you left it.</span>
              <button onClick={() => { setPolish(waiting); setParked(null); }} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }}>Open it</button>
              <button onClick={discardPolish} style={{ ...btnGhost, padding: "5px 12px", fontSize: 12 }}>Throw it away</button>
            </>) : (<>
              <span style={{ flex: 1, minWidth: 180 }}>The polished version is in.</span>
              <button onClick={() => { setAndSave(undoText); setUndoText(null); }} style={{ ...btnGhost, padding: "5px 12px", fontSize: 12 }}>Undo</button>
            </>)}
          </div>
        )}
        {polishSetup && (
          <PolishSetup opts={polishOpts} setOpts={setPolishOpts} offers={offers} offerId={offerId} setOfferId={setOfferId}
            onGo={startPolish} onClose={() => setPolishSetup(false)} />
        )}

        <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "20px 2px 40px" : "32px 8px 48px" }}>
          <textarea ref={titleRef} value={title} rows={1} aria-label="Title" placeholder="Title this post"
            onChange={(e) => { setTitle(e.target.value); soon(); }} onBlur={() => saveWords()}
            style={{ width: "100%", border: "none", background: "transparent", resize: "none", overflow: "hidden", outline: "none", padding: 0, margin: "0 0 4px", fontFamily: "inherit", fontSize: isMobile ? 24 : 30, fontWeight: 700, lineHeight: 1.25, color: B.text }} />
          <input value={subtitle} aria-label="Subtitle" placeholder="A line under the title (optional)"
            onChange={(e) => { setSubtitle(e.target.value); soon(); }} onBlur={() => saveWords()}
            style={{ width: "100%", border: "none", background: "transparent", outline: "none", padding: 0, margin: "0 0 22px", fontFamily: "inherit", fontSize: 16, color: B.mid }} />
          {/* THE PICTURE, to the right of her text under the title (her calls,
              2026-11-04: "where do I add the photo?" … "below the title and
              subtitle to the right of the text"). It lived only on the older
              Newsletter screen, which a piece she writes herself never opens.
              The same stored picture. Her title rides with the save, so a title
              still being typed is not written back over. */}
          <div style={{ display: "flex", gap: isMobile ? 12 : 20, alignItems: "flex-start" }}>
          <textarea ref={textRef} value={text} aria-label="Your piece"
            placeholder={"Just write. Your words are the post.\n\nWhen you're ready, ✦ Brill → Polish it fixes, tightens and makes it search friendly, in your voice. You see every change before it goes in."}
            onChange={(e) => { setText(e.target.value); setUndoText(null); soon(); }}
            onSelect={remember} onKeyUp={remember} onClick={remember}
            onBlur={() => { remember(); saveWords(); }}
            style={{ width: "100%", minHeight: 360, border: "none", background: "transparent", resize: "none", overflow: "hidden", outline: "none", padding: 0, fontFamily: "Lora, Georgia, serif", fontSize: isMobile ? 17 : 18, lineHeight: 1.85, color: B.text, flex: 1, minWidth: 0 }} />
            <div style={{ flex: "none", width: isMobile ? 76 : 120, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              {/* The box IS the button: press it to pick, and the picture shows
                  in place (her ask: "you could click on it and it would add a
                  picture"). Not a full preview, which differs per platform. */}
              <MediaPickButton remember="newsletter" label={newsletter.imageUrl ? "Change the picture" : "Add a picture"}
                title={newsletter.imageUrl ? "Change the picture" : "Add a picture"}
                onPick={(picked) => onSave({ ...newsletter, name: latest.current.title, imageUrl: picked || null })}>
                {newsletter.imageUrl
                  ? <img src={newsletter.imageUrl} alt="" style={{ display: "block", width: "100%", aspectRatio: "4 / 5", objectFit: "cover", borderRadius: 9, border: `1px solid ${B.border}`, background: B.surface2 }} />
                  : <span style={{ width: "100%", aspectRatio: "4 / 5", borderRadius: 9, border: `1px dashed ${B.border2}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, color: B.teal, fontSize: 12, textAlign: "center", padding: 6, boxSizing: "border-box" }}>
                      <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>Picture
                    </span>}
              </MediaPickButton>
              {newsletter.imageUrl && (
                <button onClick={() => onSave({ ...newsletter, name: latest.current.title, imageUrl: null })}
                  style={{ border: "none", background: "none", color: B.dim, cursor: "pointer", fontSize: 11.5, padding: 0, fontFamily: "inherit", textDecoration: "underline" }}>Remove</button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <button onClick={saveSelectionToLibrary} style={{ ...btnGhost, padding: "6px 11px", fontSize: 12 }}>Save selection to Library</button>
            {libSaid && <span role="status" style={{ fontSize: 12, color: B.dim }}>{libSaid}</span>}
            <span style={{ fontSize: 11.5, color: B.faint }}>A line starting “## ” is a subhead.</span>
          </div>

          {/* Where it goes from here: the blog, the cuts, a copy. */}
          {n > 0 && (
            <div style={{ marginTop: 28, paddingTop: 16, borderTop: `1px solid ${B.border}`, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {onPublish && (
                <button disabled={publishing} onClick={async () => {
                  await saveWords();
                  setPublishing(true); setPublished(null);
                  const r = await onPublish(newsletter);
                  setPublishing(false);
                  setPublished(r.ok ? { ok: true, text: `${r.updated ? "Updated" : "Published"}. Live at ${r.url.replace(/^https:\/\//, "")} in about a minute.` } : { ok: false, text: r.error });
                }} style={{ ...btnPrimary, padding: "9px 15px", fontSize: 12.5, opacity: publishing ? 0.6 : 1 }}>
                  {publishing ? "Publishing…" : isOnBlog(newsletter) ? "Update on the blog" : "Publish to blog"}
                </button>
              )}
              {onMakeVariant && ["email", "substack"].map((t) => (
                <button key={t} disabled={makingVariant === t} onClick={async () => { await saveWords(); onMakeVariant(newsletter, t); }}
                  style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
                  {makingVariant === t ? "Making…" : (newsletters || []).some((x) => x.sourceId === newsletter.id && x.type === t) ? `Another ${t === "email" ? "email" : "Substack post"}` : `Make the ${t === "email" ? "email" : "Substack post"}`}
                </button>
              ))}
              <button onClick={() => navigator.clipboard?.writeText(newsletterToText({ ...newsletter, name: title }, [{ blockType: "writing", content: { text } }]))}
                style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
                Copy the whole thing
              </button>
              {published && <div role="status" style={{ width: "100%", fontSize: 12, color: published.ok ? B.dim : B.amber }}>{published.text}</div>}
            </div>
          )}
          <RecordFooter tick={false} onDelete={() => onDelete(newsletter)} />
        </div>
      </div>

      {panelOpen && (
        <aside aria-label="Brill's suggestions" style={panelStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 12px", borderBottom: `1px solid ${B.border}` }}>
            {panel !== "preview" && <span style={{ fontSize: 12.5, fontWeight: 600, color: B.teal, background: B.tealQuiet, borderRadius: 7, padding: "6px 10px" }}>{shapeMode(panel).label.replace("…", "")}</span>}
            {panel === "preview" && <span style={{ fontSize: 12.5, fontWeight: 600, color: B.teal, background: B.tealQuiet, borderRadius: 7, padding: "6px 10px" }}>Preview</span>}
            {panel === "preview" && !isMobile && (
              <button onClick={() => setPreviewFull((v) => !v)} aria-pressed={previewFull}
                style={{ ...btnGhost, marginLeft: "auto", padding: "5px 11px", fontSize: 12 }}>
                {previewFull ? "↙ Exit full screen" : "⤢ Full screen"}
              </button>
            )}
            <button onClick={() => { setPanel(null); setPreviewFull(false); }} aria-label="Close" style={{ marginLeft: panel === "preview" && !isMobile ? 0 : "auto", border: "none", background: "none", color: B.dim, fontSize: 20, cursor: "pointer", padding: "0 6px" }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px 20px" }}>
            {panel === "preview" ? (
              previewHtml
                ? <iframe title="Preview" srcDoc={previewHtml} style={{ width: "100%", height: isMobile || full ? "calc(100vh - 90px)" : 640, border: `1px solid ${B.border}`, borderRadius: 8, background: "#fff" }} />
                : <div style={{ fontSize: 12.5, color: B.dim }}>{said && !said.ok ? <span style={{ color: B.amber }}>{said.text}</span> : "Building the page…"}</div>
            ) : (
              <ShapePanel mode={panel} busy={state === "reading"} said={said} result={result} done={done} text={text}
                offers={offers} offerId={offerId} setOfferId={setOfferId} askText={askText} setAskText={setAskText}
                picks={picks} setPicks={setPicks} titlePick={titlePick} setTitlePick={setTitlePick} currentTitle={title}
                onRun={run} onApply={tryApply} onMark={mark} onPutSearchIn={putSearchIn} onUseNextStep={useNextStep}
                onGoToText={() => { textRef.current?.focus(); if (isMobile) setPanel(null); }} />
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

/* ── What Brill proposes, by task ──────────────────────────────────────── */
const mini = (on) => ({ border: `1px solid ${on ? B.teal : B.border2}`, background: on ? B.tealQuiet : B.surface, color: on ? B.teal : B.mid, borderRadius: 6, padding: "3px 9px", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" });
const row = { display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${B.border}`, fontSize: 12.5, lineHeight: 1.5 };
const head = { margin: "14px 0 6px", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: B.mid, fontWeight: 600 };
const LEDE = {
  fix: "Real mistakes only. Your caps, your informal lines and your rough edges stay: they're the voice.",
  tighten: "Cuts only. Brill never rewrites a sentence of yours.",
  offer: "Brill's sentences, placed after one of yours. Edit them like your own once they're in.",
  search: "Your paragraphs stay exactly as you wrote them. These go around your words. Tick what you want.",
  ask: "Say what you want, in your own words. Any change waits here for you to accept.",
};

/* Only what changes, in its place: "…greater problem<s></s><b>,</b> and that…". */
function ChangeView({ find, replace }) {
  const v = changeView(find, replace);
  return (
    <span style={{ fontFamily: "Lora, Georgia, serif", fontSize: 13.5 }}>
      {v.before}
      {v.was && <s style={{ color: B.salmon, textDecorationThickness: 1 }}>{v.was}</s>}
      {v.now && <b style={{ color: B.teal, fontWeight: 600, background: B.tealQuiet, borderRadius: 3, padding: "0 1px" }}>{v.now === " " ? "␣" : v.now}</b>}
      {v.after}
    </span>
  );
}

function Status({ sid, done }) {
  const v = done[sid];
  if (!v) return null;
  return <span style={{ fontSize: 11.5, color: v === "moved" ? B.amber : B.dim }}>{v === "yes" ? "Done" : v === "no" ? "Skipped" : "That part has changed"}</span>;
}

function ShapePanel({
  mode, busy, said, result, done, text, offers, offerId, setOfferId, askText, setAskText,
  picks, setPicks, titlePick, setTitlePick, currentTitle, onRun, onApply, onMark, onPutSearchIn, onUseNextStep, onGoToText,
}) {
  const r = result || {};
  const pick = (sid) => setPicks((p) => ({ ...p, [sid]: !p[sid] }));
  return (
    <div>
      <div style={{ fontSize: 12, color: B.mid, background: B.surface2, borderRadius: 8, padding: "9px 11px", marginBottom: 10, lineHeight: 1.55 }}>{LEDE[mode]}</div>

      {mode === "offer" && (
        <div style={{ marginBottom: 10 }}>
          <div style={head}>Your offer</div>
          <select value={offerId} onChange={(e) => setOfferId(e.target.value)} style={{ ...input, marginBottom: 8 }}>
            {offers.filter((o) => o.status !== "retired").map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
          </select>
          <button disabled={busy || !offerId} onClick={() => onRun("offer", { offerId })} style={{ ...btnPrimary, padding: "7px 13px", fontSize: 12.5, opacity: busy ? 0.6 : 1 }}>
            {result ? "Ask again" : "Find the spots"}
          </button>
        </div>
      )}
      {mode === "ask" && (
        <div style={{ marginBottom: 10 }}>
          <textarea value={askText} onChange={(e) => setAskText(e.target.value)} rows={3}
            placeholder="e.g. make the ending land harder, without changing my words above it"
            style={{ ...input, resize: "vertical", lineHeight: 1.5, marginBottom: 8 }} />
          <button disabled={busy || !askText.trim()} onClick={() => onRun("ask", { ask: askText })} style={{ ...btnPrimary, padding: "7px 13px", fontSize: 12.5, opacity: busy || !askText.trim() ? 0.6 : 1 }}>Ask</button>
        </div>
      )}

      {busy && <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 0" }}>Brill is reading your piece…</div>}
      {said && <div role="status" style={{ fontSize: 12.5, color: said.ok ? B.dim : B.amber, padding: "6px 0" }}>{said.text}</div>}

      {!busy && result && mode === "fix" && (
        r.fixes?.length ? (
          <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={head}>Fixes</div>
              <button style={{ ...mini(false), marginLeft: "auto" }} onClick={() => {
                let next = text;
                for (const f of r.fixes) { if (done[f.id]) continue; const t = applyReplace(next, f.find, f.replace); if (t == null) onMark(f.id, "moved"); else { next = t; onMark(f.id, "yes"); } }
                onApply("all", next);
              }}>Fix all</button>
            </div>
            {r.fixes.map((f) => (
              <div key={f.id} style={row}>
                <span><ChangeView find={f.find} replace={f.replace} />{f.why && <small style={{ display: "block", color: B.faint }}>{f.why}</small>}</span>
                <span style={{ display: "flex", gap: 4 }}>
                  {done[f.id] ? <Status sid={f.id} done={done} /> : (<>
                    <button style={mini(true)} onClick={() => onApply(f.id, applyReplace(text, f.find, f.replace))}>Fix</button>
                    <button style={mini(false)} onClick={() => onMark(f.id, "no")}>Skip</button>
                  </>)}
                </span>
              </div>
            ))}
          </>
        ) : <div style={{ fontSize: 12.5, color: B.dim }}>Nothing to fix. It reads clean.</div>
      )}

      {!busy && result && mode === "tighten" && (
        r.cuts?.length ? r.cuts.map((c) => (
          <div key={c.id} style={row}>
            <span><s style={{ color: B.mid }}>{c.find}</s>{c.why && <small style={{ display: "block", color: B.faint }}>{c.why}</small>}</span>
            <span style={{ display: "flex", gap: 4 }}>
              {done[c.id] ? <Status sid={c.id} done={done} /> : (<>
                <button style={mini(true)} onClick={() => onApply(c.id, applyCut(text, c.find))}>Cut</button>
                <button style={mini(false)} onClick={() => onMark(c.id, "no")}>Keep</button>
              </>)}
            </span>
          </div>
        )) : <div style={{ fontSize: 12.5, color: B.dim }}>Nothing Brill would cut.</div>
      )}

      {!busy && result && mode === "offer" && (
        <>
          <div style={head}>Where it could go</div>
          {(r.inserts || []).map((x) => (
            <div key={x.id} style={{ background: B.surface2, borderRadius: 8, padding: "9px 11px", marginBottom: 8 }}>
              <small style={{ display: "block", color: B.faint, marginBottom: 3 }}>after “{x.after.length > 60 ? `…${x.after.slice(-60)}` : x.after}”</small>
              <span style={{ fontSize: 13, background: B.tealQuiet, borderRadius: 3 }}>{x.text}</span>
              <div style={{ display: "flex", gap: 4, marginTop: 7 }}>
                {done[x.id] ? <Status sid={x.id} done={done} /> : (<>
                  <button style={mini(true)} onClick={() => onApply(x.id, applyInsert(text, x.after, x.text))}>Put it in</button>
                  <button style={mini(false)} onClick={() => onMark(x.id, "no")}>Skip</button>
                </>)}
              </div>
            </div>
          ))}
          {!r.inserts?.length && <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 8 }}>No spot in the piece itself; the next step below carries it.</div>}
          {r.next && (
            <div style={{ background: B.surface2, borderRadius: 8, padding: "9px 11px" }}>
              <small style={{ display: "block", color: B.faint, marginBottom: 3 }}>the one next step, at the end</small>
              <span style={{ fontSize: 13, background: B.tealQuiet, borderRadius: 3 }}>{r.next}</span>
              <div style={{ marginTop: 7 }}><button style={mini(true)} onClick={() => onUseNextStep(r.next)}>Use as the next step</button></div>
            </div>
          )}
        </>
      )}

      {!busy && result && mode === "search" && (
        <>
          {r.titles?.length > 0 && (<>
            <div style={head}>Title</div>
            {r.titles.map((t, i) => (
              <label key={t} style={{ display: "flex", gap: 8, padding: "5px 0", fontSize: 13, cursor: "pointer" }}>
                <input type="radio" checked={titlePick === i} onChange={() => setTitlePick(i)} />
                <span>{t}{t === currentTitle && <span style={{ marginLeft: 6, fontSize: 10.5, color: B.teal }}>yours</span>}</span>
              </label>
            ))}
          </>)}
          {r.answer && (<>
            <div style={head}>The short answer</div>
            <div style={{ fontFamily: "Lora, Georgia, serif", fontSize: 13.5, lineHeight: 1.6, background: B.surface2, borderRadius: 8, padding: "9px 11px" }}>{r.answer}</div>
            <label style={{ display: "flex", gap: 8, padding: "6px 0", fontSize: 12.5 }}><input type="checkbox" checked={!!picks.answer} onChange={() => pick("answer")} />Use it</label>
          </>)}
          {r.subheads?.length > 0 && (<>
            <div style={head}>Subheads, between your paragraphs</div>
            {r.subheads.map((s) => (
              <label key={s.id} style={{ display: "flex", gap: 8, padding: "5px 0", fontSize: 13 }}>
                <input type="checkbox" checked={!!picks[s.id]} onChange={() => pick(s.id)} />
                <span>{s.text}<small style={{ display: "block", color: B.faint }}>above “{s.before.slice(0, 50)}…”</small></span>
              </label>
            ))}
          </>)}
          {r.claims?.length > 0 && (<>
            <div style={head}>What this says</div>
            {r.claims.map((c) => (
              <label key={c.id} style={{ display: "flex", gap: 8, padding: "5px 0", fontSize: 13 }}>
                <input type="checkbox" checked={!!picks[c.id]} onChange={() => pick(c.id)} /><span>{c.text}</span>
              </label>
            ))}
          </>)}
          {r.faq?.length > 0 && (<>
            <div style={head}>Questions people ask</div>
            {r.faq.map((f) => (
              <label key={f.id} style={{ display: "flex", gap: 8, padding: "5px 0", fontSize: 13 }}>
                <input type="checkbox" checked={!!picks[f.id]} onChange={() => pick(f.id)} />
                <span>{f.q}<small style={{ display: "block", color: B.faint }}>{f.a}</small></span>
              </label>
            ))}
          </>)}
          {r.gaps?.map((g) => (
            <div key={g.id} style={{ background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 9, padding: "10px 12px", fontSize: 12.5, margin: "8px 0" }}>
              <b style={{ color: B.amber }}>{g.q}</b> {g.why} Brill won't answer this for you.
              <div style={{ marginTop: 6 }}><button style={mini(false)} onClick={onGoToText}>Write a line about it</button></div>
            </div>
          ))}
          {r.pillar && (
            <label style={{ display: "flex", gap: 8, padding: "8px 0", fontSize: 12.5 }}>
              <input type="checkbox" checked={!!picks.pillar} onChange={() => pick("pillar")} />Pillar: {r.pillar}
            </label>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={onPutSearchIn} style={{ ...btnPrimary, padding: "8px 15px", fontSize: 12.5 }}>Put these in</button>
          </div>
        </>
      )}

      {!busy && result && mode === "ask" && (
        <>
          {r.reply && <div style={{ fontSize: 13, lineHeight: 1.6, padding: "6px 0 10px" }}>{r.reply}</div>}
          {(r.edits || []).map((e) => (
            <div key={e.id} style={row}>
              <span><ChangeView find={e.find} replace={e.replace} />{e.why && <small style={{ display: "block", color: B.faint }}>{e.why}</small>}</span>
              <span style={{ display: "flex", gap: 4 }}>
                {done[e.id] ? <Status sid={e.id} done={done} /> : (<>
                  <button style={mini(true)} onClick={() => onApply(e.id, applyReplace(text, e.find, e.replace))}>Accept</button>
                  <button style={mini(false)} onClick={() => onMark(e.id, "no")}>Skip</button>
                </>)}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
